import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AuthContext } from './authContext';
import { useVehicleContext } from './vehicleContext';
import { getAutoSessions } from '../services/autoSessionService';
import { fetchVehicleNegotiationStats } from '../services/vehicleService';
import { AutoNegoState, VehicleAutoNegoSession } from '../models/vehicleAutoNegoSession';
import { PushNotificationType } from '../models/user';


export interface NegotiationAlertTotals {
  unreadEmails: number;
  needHuman: number;
  waitingForClient: number;
  total: number;
}

interface NegotiationAlertsContextType {
  totals: NegotiationAlertTotals;
  loading: boolean;
  lastFetched: Date | null;
  refresh: () => Promise<void>;
}

const INITIAL_TOTALS: NegotiationAlertTotals = {
  unreadEmails: 0,
  needHuman: 0,
  waitingForClient: 0,
  total: 0,
};

const POLL_INTERVAL_MS = 60_000;


export const NEGOTIATION_ALERTS_REFRESH_EVENT = 'negotiationAlerts:refresh';

export const triggerNegotiationAlertsRefresh = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(NEGOTIATION_ALERTS_REFRESH_EVENT));
};

const NegotiationAlertsContext = createContext<NegotiationAlertsContextType>({
  totals: INITIAL_TOTALS,
  loading: false,
  lastFetched: null,
  refresh: async () => { },
});

interface NegotiationAlertsProviderProps {
  children: ReactNode;
}


export const NegotiationAlertsProvider: React.FC<NegotiationAlertsProviderProps> = ({
  children,
}) => {
  const authContext = useContext(AuthContext);
  const isLoggedIn = !!authContext?.isLoggedIn;

  const { vehicles } = useVehicleContext();

  const [totals, setTotals] = useState<NegotiationAlertTotals>(INITIAL_TOTALS);
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const vehicleIdsRef = useRef<number[]>([]);
  vehicleIdsRef.current = useMemo(() => vehicles.map((v) => v.id), [vehicles]);

  const inFlightRef = useRef<Promise<void> | null>(null);

  const pendingRefreshRef = useRef<boolean>(false);

  const refresh = useCallback(async (): Promise<void> => {
    if (!isLoggedIn) {
      setTotals(INITIAL_TOTALS);
      return;
    }
    const vehicleIds = vehicleIdsRef.current;
    if (vehicleIds.length === 0) {
      setTotals(INITIAL_TOTALS);
      return;
    }

    if (inFlightRef.current) {
      pendingRefreshRef.current = true;
      return inFlightRef.current;
    }

    const run = (async () => {
      setLoading(true);
      try {
        const { autoNegoSessions } = await getAutoSessions(vehicleIds);

        const activeVehicleIds: number[] = [];
        Object.entries(autoNegoSessions).forEach(([id, session]) => {
          const s = session as VehicleAutoNegoSession | null;
          if (!s) return;
          const hasActive = (s.currentActiveNegos ?? 0) > 0;
          const running =
            s.autoNegoState === AutoNegoState.ON ||
            s.autoNegoState === AutoNegoState.WAITING_RESULTS ||
            s.autoNegoState === AutoNegoState.RESULT_TO_REVIEW;
          if (hasActive || running) {
            activeVehicleIds.push(Number(id));
          }
        });

        if (activeVehicleIds.length === 0) {
          setTotals(INITIAL_TOTALS);
          setLastFetched(new Date());
          return;
        }

        const statsResults = await Promise.all(
          activeVehicleIds.map((id) => fetchVehicleNegotiationStats(id)),
        );

        let unreadEmails = 0;
        let needHuman = 0;
        let waitingForClient = 0;
        statsResults.forEach((stats) => {
          if (!stats) return;
          unreadEmails += stats.unreadEmails;
          needHuman += stats.needHuman;
          waitingForClient += stats.waitingForClient;
        });

        setTotals({
          unreadEmails,
          needHuman,
          waitingForClient,
          total: unreadEmails + needHuman,
        });
        setLastFetched(new Date());
      } catch {
        // Errors are intentionally swallowed; individual API helpers
        // already handle their own toasts where appropriate.
      } finally {
        setLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = run;
    try {
      await run;
    } finally {

      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        refresh();
      }
    }
  }, [isLoggedIn]);

  // Kick off an initial fetch + poll whenever the user is logged in and
  // we have a non-empty fleet list.
  useEffect(() => {
    if (!isLoggedIn) {
      setTotals(INITIAL_TOTALS);
      setLastFetched(null);
      return;
    }
    if (vehicles.length === 0) return;

    refresh();
    const interval = setInterval(() => {
      refresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isLoggedIn, vehicles.length, refresh]);


  useEffect(() => {
    const handleRefreshEvent = () => {
      refresh();
    };
    window.addEventListener(
      NEGOTIATION_ALERTS_REFRESH_EVENT,
      handleRefreshEvent,
    );
    return () => {
      window.removeEventListener(
        NEGOTIATION_ALERTS_REFRESH_EVENT,
        handleRefreshEvent,
      );
    };
  }, [refresh]);


  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const triggerTypes: PushNotificationType[] = [
      PushNotificationType.NEGOTIATION_EMAIL_RECEIVED,
      PushNotificationType.NEGOTIATION_HUMAN_REQUIRED,
      PushNotificationType.NEGOTIATION_VALIDATION_REQUIRED,
    ];
    const handleMessage = (event: MessageEvent) => {
      if (triggerTypes.includes(event.data?.type)) {
        refresh();
      }
    };
    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [refresh]);

  const value = useMemo<NegotiationAlertsContextType>(
    () => ({ totals, loading, lastFetched, refresh }),
    [totals, loading, lastFetched, refresh],
  );

  return (
    <NegotiationAlertsContext.Provider value={value}>
      {children}
    </NegotiationAlertsContext.Provider>
  );
};

/**
 * Hook for consuming live fleet-wide negotiation alert totals.
 */
export const useNegotiationAlerts = (): NegotiationAlertsContextType => useContext(NegotiationAlertsContext);
